/**
 * CashFlowSummaryCards Component
 *
 * Supports three layout variants controlled by the `layout` prop:
 *  - 'period-cards' (default): 4 KPI cards in a grid
 *  - 'hero': large left hero card + right column chip cards
 *  - 'compact': single horizontal strip of metrics
 */

import React from 'react';
import {
    Box,
    Grid,
    GridItem,
    Text,
    VStack,
    HStack,
    Divider,
    Skeleton,
    Flex,
} from '@chakra-ui/react';
import { CashFlowSummary, SummaryLayout } from '../types';
import { formatNumber } from '../utils';

const REV_COLOR = '#1E8A4E';
const COST_COLOR = '#BD4A30';
const HERO_BG = 'linear-gradient(135deg, #0F5C32, #157A43)';

interface CashFlowSummaryCardsProps {
    summary: CashFlowSummary | null;
    loading: boolean;
    layout?: SummaryLayout;
    companyName?: string;
}

const StatRow: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
    <HStack justify="space-between" w="100%">
        <Text fontSize="xs" color="gray.500">{label}</Text>
        <Text fontSize="sm" fontWeight="semibold" color={color} fontFamily="mono">
            {formatNumber(value)}
        </Text>
    </HStack>
);

const InfoCard: React.FC<{
    title: string;
    headline: number;
    headlineColor?: string;
    children?: React.ReactNode;
}> = ({ title, headline, headlineColor, children }) => (
    <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100" h="100%">
        <VStack align="stretch" spacing={2}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                {title}
            </Text>
            <Text fontSize="xl" fontWeight="bold" fontFamily="mono" color={headlineColor || 'gray.800'}>
                {formatNumber(headline)}
            </Text>
            {children && (
                <>
                    <Divider borderStyle="dashed" />
                    {children}
                </>
            )}
        </VStack>
    </Box>
);

const ChipCard: React.FC<{ title: string; value: number; color?: string }> = ({ title, value, color }) => (
    <Box bg="gray.50" px={3} py={2} borderRadius="lg" border="1px" borderColor="gray.100" flex="1">
        <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>{title}</Text>
        <Text fontSize="sm" fontWeight="bold" fontFamily="mono" color={color || 'gray.800'}>{formatNumber(value)}</Text>
    </Box>
);

const MetricCell: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
    <Box flex="1" px={3} py={2} borderRight="1px" borderColor="gray.100" _last={{ borderRight: 'none' }}>
        <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
        <Text fontSize="sm" fontWeight="bold" fontFamily="mono" color={color || 'gray.800'}>{formatNumber(value)}</Text>
    </Box>
);

// ─── Loading skeleton ────────────────────────────────────────────────────────

const LoadingSkeleton: React.FC<{ layout: SummaryLayout }> = ({ layout }) => {
    if (layout === 'compact') return <Skeleton height="56px" borderRadius="xl" />;
    if (layout === 'hero') return (
        <Grid templateColumns={{ base: '1fr', lg: '1.4fr 1fr' }} gap={4}>
            <Skeleton height="160px" borderRadius="xl" />
            <VStack spacing={3}><Skeleton height="72px" borderRadius="xl" /><Skeleton height="72px" borderRadius="xl" /></VStack>
        </Grid>
    );
    return (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height="140px" borderRadius="xl" />)}
        </Grid>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

const CashFlowSummaryCards: React.FC<CashFlowSummaryCardsProps> = ({
    summary,
    loading,
    layout = 'period-cards',
    companyName,
}) => {
    if (loading) return <LoadingSkeleton layout={layout} />;

    const s: CashFlowSummary = summary || ({
        total_revenue_budget: 0,
        total_revenue_realized: 0,
        total_cost_budget: 0,
        total_cost_realized: 0,
        net_budget: 0,
        net_realized: 0,
        financing_in: 0,
        opening: 0,
        ending_balance_realized: 0,
        opening_balance_unrealized: 0,
        ending_balance_unrealized: 0,
    } as CashFlowSummary);

    const netColor = s.net_realized < 0 ? COST_COLOR : REV_COLOR;
    const saldoColor = s.ending_balance_realized < 0 ? COST_COLOR : REV_COLOR;

    // ── Layout: Period Cards ─────────────────────────────────────────────────
    if (layout === 'period-cards') {
        return (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
                <GridItem>
                    <Box p={4} borderRadius="xl" boxShadow="sm" bgGradient={HERO_BG} color="white" h="100%">
                        <VStack align="stretch" spacing={2}>
                            <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" opacity={0.85}>
                                Net Cashflow (Realized)
                            </Text>
                            <Text fontSize="2xl" fontWeight="bold" fontFamily="mono">
                                {formatNumber(s.net_realized)}
                            </Text>
                            <Divider borderColor="whiteAlpha.400" borderStyle="dashed" />
                            <HStack justify="space-between">
                                <Text fontSize="xs" opacity={0.85}>Net Budget</Text>
                                <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">
                                    {formatNumber(s.net_budget)}
                                </Text>
                            </HStack>
                        </VStack>
                    </Box>
                </GridItem>

                <InfoCard title="Total Revenue (Realized)" headline={s.total_revenue_realized} headlineColor={REV_COLOR}>
                    <StatRow label="Budget" value={s.total_revenue_budget} />
                </InfoCard>

                <InfoCard title="Total Cost (Realized)" headline={s.total_cost_realized} headlineColor={COST_COLOR}>
                    <StatRow label="Budget" value={s.total_cost_budget} />
                </InfoCard>

                <InfoCard title="Saldo Akhir (Realized)" headline={s.ending_balance_realized} headlineColor={saldoColor}>
                    <StatRow label="Saldo Awal" value={s.opening} />
                    <StatRow label="Saldo Awal Unrealized" value={s.opening_balance_unrealized} />
                    <StatRow label="Saldo Akhir Unrealized" value={s.ending_balance_unrealized} />
                </InfoCard>
            </Grid>
        );
    }

    // ── Layout: Hero KPI ────────────────────────────────────────────────────
    if (layout === 'hero') {
        return (
            <Grid templateColumns={{ base: '1fr', lg: '1.4fr 1fr' }} gap={4}>
                <GridItem>
                    <Box p={6} borderRadius="xl" boxShadow="md" bgGradient={HERO_BG} color="white" h="100%">
                        <VStack align="stretch" spacing={3}>
                            {companyName && (
                                <Text fontSize="xs" opacity={0.75} textTransform="uppercase" letterSpacing="wide">
                                    {companyName}
                                </Text>
                            )}
                            <Box>
                                <Text fontSize="xs" opacity={0.75} textTransform="uppercase" letterSpacing="wide" mb={1}>
                                    Net Cashflow (Realized)
                                </Text>
                                <Text fontSize="4xl" fontWeight="bold" fontFamily="mono" lineHeight={1.1}>
                                    {formatNumber(s.net_realized)}
                                </Text>
                            </Box>
                            <Divider borderColor="whiteAlpha.300" borderStyle="dashed" />
                            <HStack spacing={6}>
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="10px" opacity={0.7}>Revenue</Text>
                                    <Text fontSize="lg" fontWeight="semibold" fontFamily="mono" color="#86efac">
                                        {formatNumber(s.total_revenue_realized)}
                                    </Text>
                                </VStack>
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="10px" opacity={0.7}>Cost</Text>
                                    <Text fontSize="lg" fontWeight="semibold" fontFamily="mono" color="#fca5a5">
                                        {formatNumber(s.total_cost_realized)}
                                    </Text>
                                </VStack>
                            </HStack>
                            <HStack justify="space-between">
                                <Text fontSize="xs" opacity={0.7}>Net Budget</Text>
                                <Text fontSize="sm" fontFamily="mono" fontWeight="semibold">
                                    {formatNumber(s.net_budget)}
                                </Text>
                            </HStack>
                        </VStack>
                    </Box>
                </GridItem>

                <GridItem>
                    <VStack spacing={3} h="100%">
                        <Grid templateColumns="repeat(2, 1fr)" gap={3} w="100%">
                            <ChipCard title="Revenue Budget" value={s.total_revenue_budget} color={REV_COLOR} />
                            <ChipCard title="Cost Budget" value={s.total_cost_budget} color={COST_COLOR} />
                        </Grid>
                        <Grid templateColumns="repeat(2, 1fr)" gap={3} w="100%">
                            <ChipCard title="Saldo Akhir Realized" value={s.ending_balance_realized} color={saldoColor} />
                            <ChipCard title="Saldo Awal Unrealized" value={s.opening_balance_unrealized} color={saldoColor} />
                        </Grid>
                        <Box
                            bg="white"
                            border="1px"
                            borderColor="gray.100"
                            borderRadius="xl"
                            p={3}
                            w="100%"
                            boxShadow="sm"
                        >
                            <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
                                Saldo Akhir Unrealized
                            </Text>
                            <Text fontSize="xl" fontWeight="bold" fontFamily="mono" color={s.ending_balance_unrealized < 0 ? COST_COLOR : REV_COLOR}>
                                {formatNumber(s.ending_balance_unrealized)}
                            </Text>
                            <HStack mt={2} justify="space-between">
                                <Text fontSize="xs" color="gray.500">Saldo Awal</Text>
                                <Text fontSize="xs" fontFamily="mono" color="gray.600">{formatNumber(s.opening)}</Text>
                            </HStack>
                        </Box>
                    </VStack>
                </GridItem>
            </Grid>
        );
    }

    // ── Layout: Compact Strip ────────────────────────────────────────────────
    return (
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100" overflow="hidden">
            <Flex wrap="wrap">
                <MetricCell label="Net Cashflow" value={s.net_realized} color={netColor} />
                <MetricCell label="Revenue" value={s.total_revenue_realized} color={REV_COLOR} />
                <MetricCell label="Cost" value={s.total_cost_realized} color={COST_COLOR} />
                <MetricCell label="Saldo Akhir Realized" value={s.ending_balance_realized} color={saldoColor} />
                <MetricCell label="Saldo Awal Unrealized" value={s.opening_balance_unrealized} />
                <MetricCell label="Saldo Akhir Unrealized" value={s.ending_balance_unrealized} />
            </Flex>
        </Box>
    );
};

export default CashFlowSummaryCards;
